import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Gamepad2 } from 'lucide-react';
import { PageLoadingIndicator } from '@kioskgaming/page-loading';
import { formatInTimezone, useTimezone } from '@kioskgaming/ui';
import { getShopGameWallets } from '../../services/api';
import type { ShopGameWallet } from '../../types';
import GameWalletDrawer from '../../pages/gameWallets/GameWalletDrawer';
import { GAME_WALLET_SYNC_LABELS } from '../../utils/gameWalletSync';

const SYNC_LABELS = GAME_WALLET_SYNC_LABELS;

const SYNC_BADGE: Record<ShopGameWallet['syncStatus'], string> = {
  in_sync: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  failed: 'bg-red-50 text-red-700 border-red-200',
  never_synced: 'bg-amber-50 text-amber-700 border-amber-200',
};

type Props = {
  playerId: string;
};

export default function PlayerGameWalletsSection({ playerId }: Props) {
  const { timezone } = useTimezone();
  const [wallets, setWallets] = useState<ShopGameWallet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ShopGameWallet | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getShopGameWallets({ playerId, limit: 100 })
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data) {
          setWallets(res.data.items);
        } else {
          setWallets([]);
          setError(res.message || 'Could not load game wallets');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setWallets([]);
          setError('Could not load game wallets');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [playerId]);

  return (
    <section className="rounded-2xl border border-gray-200/90 bg-white p-4 shadow-sm sm:p-6">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-stone-100 text-stone-600">
            <Gamepad2 className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900">Game wallets</h3>
            <p className="text-xs text-gray-500">Current wallets for this Player</p>
          </div>
        </div>
        <Link
          to="/game-wallets"
          className="text-xs font-semibold text-emerald-700 hover:text-emerald-900"
        >
          All game wallets
        </Link>
      </div>

      {loading ? (
        <div className="py-8">
          <PageLoadingIndicator variant="inline" label="Loading…" accentColor="#059669" />
        </div>
      ) : error ? (
        <p className="py-6 text-center text-sm text-red-600">{error}</p>
      ) : wallets.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/80 py-10 text-center">
          <p className="text-sm text-gray-500">No game wallets for this Player yet.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-left">
            <thead>
              <tr className="border-b border-stone-100 text-xs font-semibold text-stone-500">
                <th className="px-2 py-2">Provider</th>
                <th className="px-2 py-2 text-right">In-game</th>
                <th className="px-2 py-2 text-right">Temp wallet</th>
                <th className="px-2 py-2">Sync</th>
                <th className="px-2 py-2">Last sync</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {wallets.map((wallet) => (
                <tr
                  key={wallet.id}
                  className="cursor-pointer hover:bg-stone-50"
                  onClick={() => setSelected(wallet)}
                >
                  <td className="px-2 py-3">
                    <div className="text-sm font-medium text-stone-900">
                      {wallet.providerName || wallet.providerCode || wallet.providerId}
                    </div>
                    {wallet.providerCode && wallet.providerName ? (
                      <div className="text-xs text-stone-400">{wallet.providerCode}</div>
                    ) : null}
                  </td>
                  <td className="px-2 py-3 text-right font-mono text-sm tabular-nums text-stone-800">
                    {wallet.gameWalletBalance.toFixed(2)}
                  </td>
                  <td className="px-2 py-3 text-right font-mono text-sm tabular-nums text-stone-800">
                    {wallet.masterCreditBalance.toFixed(2)}
                  </td>
                  <td className="px-2 py-3">
                    <span
                      className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${SYNC_BADGE[wallet.syncStatus]}`}
                    >
                      {SYNC_LABELS[wallet.syncStatus]}
                    </span>
                  </td>
                  <td className="px-2 py-3 text-sm text-stone-500">
                    {wallet.lastSyncAt ? formatInTimezone(wallet.lastSyncAt, timezone) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <GameWalletDrawer wallet={selected} open={!!selected} onClose={() => setSelected(null)} />
    </section>
  );
}
